import React from "react";
import { Box, Typography, Avatar, IconButton, Tabs, Tab, alpha } from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { COLORS } from "../../styles/uiConstants";
import { useClinicTheme } from "../../theme";

/**
 * Detay sayfası üst bandı (klinik detay, hasta detay, muayene): geri butonu +
 * avatar/logo + ad + chip'ler + aksiyonlar, opsiyonel olarak altında sekme
 * satırı. Ölçüler `PageHeaderBar` ile aynı (px {xs:2, md:4}, py 2.5, başlık
 * 1.125rem/700, beyaz zemin + alt çizgi); sekmeler `TabbedPageShell` ile aynı
 * görünümde. Sekme durumu kontrollüdür — URL senkronu çağıranın işidir.
 *
 * @param {() => void} [onBack] - Verilirse solda "Geri" butonu çizilir
 * @param {{src?: string, initials?: string, icon?: React.ReactNode, alt?: string}} [avatar]
 *   src (logo/fotoğraf) > initials > icon önceliğiyle çizilir
 * @param {React.ReactNode} title
 * @param {React.ReactNode} [subtitle]
 * @param {React.ReactNode[]} [chips] - Başlık yanındaki durum chip'leri
 * @param {React.ReactNode} [actions] - Sağ taraftaki kontroller
 * @param {Array<{key: string, label: React.ReactNode, icon?: React.ReactNode}>} [tabs]
 * @param {string} [activeTab] - Aktif sekmenin key'i (yoksa ilk sekme)
 * @param {(key: string) => void} [onTabChange]
 */
export default function EntityHeader({
  onBack,
  avatar,
  title,
  subtitle,
  chips,
  actions,
  tabs,
  activeTab,
  onTabChange,
}) {
  const { palette } = useClinicTheme();
  const tone = palette?.primary || COLORS.slate700;
  const hasTabs = Array.isArray(tabs) && tabs.length > 0;
  const tabIndex = hasTabs ? Math.max(0, tabs.findIndex((t) => t.key === activeTab)) : 0;
  const chipList = (Array.isArray(chips) ? chips : chips ? [chips] : []).filter(Boolean);

  return (
    <Box
      component="header"
      sx={{ flexShrink: 0, bgcolor: COLORS.white, borderBottom: `1px solid ${COLORS.slate200}` }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          py: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        {onBack ? (
          <IconButton
            onClick={onBack}
            aria-label="Geri"
            sx={{
              width: 38,
              height: 38,
              border: `1px solid ${COLORS.slate200}`,
              borderRadius: 2,
              color: COLORS.slate600,
              "&:hover": { bgcolor: COLORS.slate50, color: tone },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
        ) : null}

        {avatar ? (
          <Avatar
            variant="rounded"
            src={avatar.src || undefined}
            alt={avatar.alt || (typeof title === "string" ? title : undefined)}
            sx={{
              width: 44,
              height: 44,
              border: `1px solid ${COLORS.slate200}`,
              bgcolor: avatar.src ? COLORS.white : alpha(tone, 0.08),
              color: tone,
              fontSize: "0.95rem",
              fontWeight: 700,
              "& img": { objectFit: "contain" },
            }}
          >
            {avatar.src ? null : avatar.initials || avatar.icon || null}
          </Avatar>
        ) : null}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0 }}>
            <Typography
              variant="h6"
              component="h1"
              noWrap
              sx={{
                fontWeight: 700,
                fontSize: "1.125rem",
                color: COLORS.slate800,
                lineHeight: 1.3,
                minWidth: 0,
              }}
            >
              {title}
            </Typography>
            {chipList.map((chip, i) => (
              <React.Fragment key={i}>{chip}</React.Fragment>
            ))}
          </Box>
          {subtitle ? (
            <Typography
              variant="caption"
              component="p"
              sx={{ color: COLORS.slate400, fontSize: "0.75rem" }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>

        {actions ? (
          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
            {actions}
          </Box>
        ) : null}
      </Box>

      {hasTabs ? (
        <Tabs
          value={tabIndex}
          onChange={(_, idx) => onTabChange?.(tabs[idx].key)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: { xs: 2, md: 4 },
            minHeight: 42,
            "& .MuiTab-root": {
              color: COLORS.slate400,
              fontWeight: 600,
              fontSize: "0.8125rem",
              textTransform: "none",
              minHeight: 42,
              py: 1,
              px: 1.5,
              minWidth: "auto",
              "&.Mui-selected": { color: tone },
              "&:hover": { color: COLORS.slate700 },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: tone,
              height: 2,
              borderRadius: "2px 2px 0 0",
            },
            "& .MuiTabs-scrollButtons": { color: COLORS.slate400, width: 28 },
          }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.key}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  {tab.icon ? (
                    <span style={{ lineHeight: 1, display: "flex", alignItems: "center" }}>
                      {tab.icon}
                    </span>
                  ) : null}
                  {tab.label}
                </Box>
              }
            />
          ))}
        </Tabs>
      ) : null}
    </Box>
  );
}
